/**
 * @file Guide generator types.
 *
 * Contains Zod schemas and types for guide generation that are safe to import
 * from client components. Server-only code is in `GuideGenerator.ts`.
 */

import { z } from "zod";

import { AiApiKey, AiConfig, AiUsage } from "@/lib/core/ai/types";
import { Fluency, GuideLength, Locale } from "@/lib/core/payload/enums";

/**
 * Input parameters for guide generation.
 */
export const GuideGeneratorInput = z
  .object({
    ai: AiConfig,
    aiApiKey: AiApiKey,

    audienceRoleId: z
      .uuid()
      .optional()
      .describe(`Audience role ID. The role title tailors the guide.`),

    fluency: Fluency.optional().describe(
      `Target fluency level for the generated guide.`,
    ),

    length: GuideLength.describe(`Target length for the generated guide.`),

    locale: Locale.optional().describe(
      `Target locale/language for the generated guide.`,
    ),

    variantIdList: z
      .array(z.uuid())
      .min(1)
      .describe(`Guide variant IDs to merge/adapt. At least one required.`),
  })
  .describe(`Input parameters for guide generation.`);
export type GuideGeneratorInput = z.infer<typeof GuideGeneratorInput>;

/**
 * Schema for AI-generated guide content.
 */
export const GuideGeneratorOutput = z.object({
  contentMarkdown: z
    .string()
    .describe(`The main guide content in Markdown format.`),

  referenceListMarkdown: z
    .string()
    .describe(
      `A list of references to the source materials in Markdown format.`,
    ),

  summaryMarkdown: z
    .string()
    .describe(`A brief 2-3 sentence summary/teaser of the guide content.`),

  title: z.string().describe(`A clear, descriptive title for the guide.`),
});
export type GuideGeneratorOutput = z.infer<typeof GuideGeneratorOutput>;

/**
 * Result of guide generation (non-streaming).
 */
export const GuideGeneratorResult = GuideGeneratorOutput.extend({
  usage: AiUsage.optional(),
}).describe(`Result of guide generation.`);
export type GuideGeneratorResult = z.infer<typeof GuideGeneratorResult>;
